import { motion } from "framer-motion";
import { useTranslation } from "@/packages/utils";
import { springs } from "../ui/motion";
import { getNotificationConfig } from "./notificationConfig";
import { formatRelativeTime } from "../ui/ActivityFeed/ActivityItem";

interface Props {
  category: string;
  title: string;
  createdAt: number;
  relatedEntityName: string;
}

export function NotificationDetailHeader({
  category,
  title,
  createdAt,
  relatedEntityName,
}: Props) {
  const { t } = useTranslation("inbox");
  const config = getNotificationConfig(category);
  const Icon = config.icon;

  return (
    <div className="flex items-start gap-4">
      {/* Icon badge with breathing glow */}
      <div className="relative shrink-0 mt-0.5">
        <motion.div
          className="absolute -inset-1 rounded-full"
          style={{ backgroundColor: `${config.color}08` }}
          animate={{ opacity: [0.4, 0.8, 0.4] }}
          transition={{
            duration: 3,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="relative w-11 h-11 rounded-full flex items-center justify-center"
          style={{ backgroundColor: config.bgColor }}
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ ...springs.bouncy, delay: 0.02 }}
        >
          <Icon size={18} style={{ color: config.color }} />
        </motion.div>
      </div>

      {/* Title + meta */}
      <div className="flex-1 min-w-0 pt-0.5">
        <motion.h2
          className="text-[16px] font-semibold text-primary leading-snug tracking-[-0.01em]"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springs.smooth, delay: 0.06 }}
        >
          {t(`notificationTitles.${category}`, { defaultValue: title })}
        </motion.h2>

        <motion.div
          className="flex items-center gap-1.5 mt-1.5 flex-wrap"
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springs.smooth, delay: 0.12 }}
        >
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{
              backgroundColor: `${config.color}0F`,
              color: config.color,
            }}
          >
            {t(config.labelKey)}
          </span>
          <span className="text-[11px] text-muted truncate max-w-[180px]">
            {relatedEntityName}
          </span>
          <span className="w-[3px] h-[3px] rounded-full bg-border shrink-0" />
          <span className="text-[11px] text-muted shrink-0">
            {formatRelativeTime(new Date(createdAt))}
          </span>
        </motion.div>
      </div>
    </div>
  );
}
